<script lang="ts">
  /**
   * MySetsSection — "My sets" on the Scores tab: the sets saved from a friend's
   * link (mySets.ts), newest first. Hidden until there is one: an empty section
   * would advertise something only a friend's link can fill.
   *
   * One row per set in the "Your sets" tap-row chrome (name; from · metres ·
   * saved date). Tapping a row opens it in place: the set as the share card
   * (MySetCardImage) and "Remove from my sets".
   */
  import { mySets, removeMySet } from "./mySets";
  import { fmtMeters } from "../format";
  import MySetCardImage from "./MySetCardImage.svelte";

  let open = $state<string | null>(null);

  function toggle(signature: string) {
    open = open === signature ? null : signature;
  }

  function remove(signature: string) {
    if (removeMySet(signature) && open === signature) open = null;
  }

  function savedDay(iso: string): string {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }

  function meta(from: string | null, totalM: number, savedAt: string): string {
    const day = savedDay(savedAt);
    return [from ? `from ${from}` : null, fmtMeters(totalM), day ? `saved ${day}` : null].filter(Boolean).join(" · ");
  }
</script>

{#if $mySets.length > 0}
  <section class="my-sets" aria-label="My sets">
    <h2 class="sec-label">My sets</h2>
    <!-- One line of context (VP panel, polish round): right under "Your sets" the
         heading alone read as the same list twice. -->
    <!-- Scott + panel ruling 2026-09-11: keeping a set stays link-only; "on this
         phone only" is the whole next step, folded into this line (VP round 3). -->
    <p class="explainer">Sets saved from a link someone shared with you, on this phone only. Tap one to see it.</p>
    <ul class="rows">
      {#each $mySets as s (s.signature)}
        <li class="item" data-my-set>
          <button
            type="button"
            class="tap-row"
            aria-expanded={open === s.signature}
            onclick={() => toggle(s.signature)}
          >
            <span class="text">
              <span class="t">{s.card.set.name}</span>
              <span class="s">{meta(s.from, s.card.set.totalM, s.savedAt)}</span>
            </span>
            <span class="chev" class:down={open === s.signature} aria-hidden="true">›</span>
          </button>
          {#if open === s.signature}
            <div class="opened">
              <MySetCardImage card={s.card} />
              <button type="button" class="remove" onclick={() => remove(s.signature)}>Remove from my sets</button>
            </div>
          {/if}
        </li>
      {/each}
    </ul>
  </section>
{/if}

<style>
  .my-sets {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  /* Same section-label tier as the rest of the Scores tab. */
  .sec-label {
    font: var(--type-title-card);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--text-muted);
    margin: var(--space-2) 2px calc(-1 * var(--space-1));
  }
  /* Same explainer tier as "Your sets" (YourSetsSection .explainer). */
  .explainer {
    margin: 0 2px;
    font-size: 13px;
    line-height: 1.45;
    color: var(--text-muted);
  }
  .rows {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .item {
    background: var(--bg-card);
    border: 1px solid var(--divider);
    border-radius: var(--radius-card);
    overflow: hidden;
  }
  /* The "Your sets" tap row (YourSetsSection), as a real button. */
  .tap-row {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
    background: transparent;
    border: 0;
    padding: 13px 16px;
    min-height: 44px;
    font: inherit;
    color: inherit;
    text-align: left;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .tap-row:active {
    background: var(--bg-raised);
  }
  .tap-row:focus-visible {
    outline: 2px solid var(--accent-primary);
    outline-offset: -2px;
  }
  .text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .t {
    font-weight: 500;
    color: var(--text-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .s {
    font-size: 13px;
    color: var(--text-muted);
    margin-top: 2px;
  }
  .chev {
    color: var(--text-muted);
    font-size: 22px;
    line-height: 1;
    transition: transform var(--dur-micro) var(--ease-swim);
  }
  .chev.down {
    transform: rotate(90deg);
  }
  .opened {
    padding: 0 16px 8px;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .remove {
    align-self: flex-start;
    min-height: 44px;
    margin-left: calc(-1 * var(--space-2));
    padding: var(--space-2);
    border: 0;
    background: transparent;
    font-family: inherit;
    font-size: 15px;
    font-weight: 600;
    color: var(--text-muted);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .remove:focus-visible {
    outline: 2px solid var(--accent-primary);
    outline-offset: 2px;
    border-radius: var(--radius-button);
  }
</style>
